import { motion } from "framer-motion";
import { useIsMobile } from "@/hooks/use-mobile";
import { useVisibleMotion } from "@/hooks/use-visible-motion";

/**
 * Subtle animated flowing curved lines for page ambience.
 * Fintech/enterprise style — like data flowing through circuits.
 */

interface FlowLineProps {
  d: string;
  duration: number;
  delay: number;
  color?: string;
  width?: number;
  active?: boolean;
}

const FlowLine = ({ d, duration, delay, color = "hsl(var(--primary) / 0.07)", width = 1, active = false }: FlowLineProps) => (
  <motion.path
    d={d}
    fill="none"
    stroke={color}
    strokeWidth={width}
    strokeLinecap="round"
    initial={false}
    animate={active ? { pathLength: [0, 1, 1, 0], opacity: [0, 0.6, 0.6, 0] } : { pathLength: 1, opacity: 0.4 }}
    transition={{
      duration,
      delay,
      repeat: Infinity,
      repeatDelay: duration * 0.3,
      ease: "easeInOut",
      times: [0, 0.4, 0.7, 1],
    }}
  />
);

/** A traveling dot that follows a path */
const FlowDot = ({ d, duration, delay, color = "hsl(var(--primary) / 0.15)" }: Omit<FlowLineProps, "width">) => (
  <motion.circle r="2" fill={color}>
    <animateMotion dur={`${duration}s`} begin={`${delay}s`} repeatCount="indefinite" path={d} />
  </motion.circle>
);

export const FlowLines = ({ variant = "default", className = "" }: { variant?: "default" | "hero" | "dark"; className?: string }) => {
  const isMobile = useIsMobile();
  const { ref, active } = useVisibleMotion<HTMLDivElement>();
  if (isMobile) return null;
  const isHero = variant === "hero";
  const isDark = variant === "dark" || variant === "hero";

  const lineColor = isDark ? "hsl(200 100% 55% / 0.06)" : "hsl(var(--primary) / 0.05)";
  const dotColor = isDark ? "hsl(200 100% 55% / 0.12)" : "hsl(var(--primary) / 0.1)";
  const accentColor = isDark ? "hsl(270 60% 65% / 0.04)" : "hsl(var(--primary) / 0.03)";

  return (
    <div ref={ref} className={`absolute inset-0 pointer-events-none overflow-hidden ${className}`}>
      <svg
        className="absolute inset-0 w-full h-full"
        viewBox="0 0 1400 900"
        preserveAspectRatio="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Primary flowing curves - left to right */}
        <FlowLine d="M -50 200 C 200 180, 400 350, 600 300 S 1000 200, 1450 280" duration={12} delay={0} color={lineColor} />
        <FlowLine d="M -50 500 C 150 450, 350 600, 550 520 S 900 400, 1450 500" duration={14} delay={2} color={lineColor} />
        <FlowLine d="M -50 700 C 300 680, 500 750, 700 700 S 1100 620, 1450 720" duration={16} delay={4} color={accentColor} width={1.5} />

        {/* Secondary curves - diagonal flow */}
        <FlowLine d="M 100 -50 C 150 200, 300 400, 500 450 S 800 500, 1000 900" duration={18} delay={1} color={accentColor} />
        <FlowLine d="M 1200 -50 C 1150 150, 1000 300, 800 380 S 500 500, 300 950" duration={15} delay={3} color={lineColor} />

        {/* Only one moving accent, and only while onscreen. */}
        {active && <FlowDot d="M -50 200 C 200 180, 400 350, 600 300 S 1000 200, 1450 280" duration={10} delay={1} color={dotColor} />}

        {isHero && (
          <>
            <FlowLine d="M -50 350 C 250 320, 450 450, 650 380 S 950 300, 1450 380" duration={13} delay={5} color={lineColor} />
          </>
        )}
      </svg>
    </div>
  );
};

export default FlowLines;
